(() => {
  'use strict';
  if (customElements.get('preme-gut-quiz')) return;

  class PremeGutQuiz extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.answers = {};
      this.questionIndex = 0;
      this.contactCaptured = false;
      this.capturePending = false;
      this.core = window.PremeGutQuizCore;
      this.screens = [...this.querySelectorAll('[data-screen]')];
      this.questionForm = this.querySelector('[data-screen="question"]');
      this.captureForm = this.querySelector('[data-screen="capture"]');
      this.imageURLs = {};
      for (const image of this.querySelector('[data-choice-image-urls]').content.querySelectorAll('img')) {
        this.imageURLs[image.dataset.asset] = image.src;
      }
      this.preview = this.dataset.preview === 'true';
      this.bindEvents();
      try {
        const response = await fetch(this.dataset.source, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('Quiz data could not be loaded.');
        this.data = await response.json();
        if (!this.core || this.data.questions.length !== 12 || this.data.outcomes.length !== 9) {
          throw new Error('Quiz data is incomplete.');
        }
        this.querySelector('[data-start]').disabled = false;
        this.querySelector('[data-start]').textContent = 'start my gut check →';
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.querySelector('[data-load-error]').hidden = false;
          this.querySelector('[data-start]').textContent = 'quiz unavailable';
        }
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.initialized = false;
    }

    bindEvents() {
      const options = { signal: this.abortController.signal };
      this.querySelector('[data-start]').addEventListener('click', () => {
        if (this.dataset.capturePosition === 'beginning' && !this.contactCaptured) this.showCapture();
        else this.showQuestion(0);
      }, options);
      this.questionForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const selected = this.selectedChoices();
        const question = this.data.questions[this.questionIndex];
        if (!selected.length) {
          this.questionError('Choose an answer, or use “skip question” to move on.');
          return;
        }
        if (selected.length < question.min || selected.length > question.max) {
          this.questionError(`Please choose between ${Math.max(1, question.min)} and ${question.max} answers.`);
          return;
        }
        this.answers[question.id] = selected;
        this.advance();
      }, options);
      this.querySelector('[data-skip]').addEventListener('click', () => {
        this.answers[this.data.questions[this.questionIndex].id] = [];
        this.advance();
      }, options);
      this.querySelector('[data-question-back]').addEventListener('click', () => {
        this.saveCurrentSelections();
        if (this.questionIndex > 0) this.showQuestion(this.questionIndex - 1);
        else this.showScreen('welcome');
      }, options);
      this.questionForm.addEventListener('change', (event) => {
        if (!event.target.matches('[data-choice]')) return;
        const question = this.data.questions[this.questionIndex];
        if (this.selectedChoices().length > question.max) {
          event.target.checked = false;
          this.questionError(`You can choose up to ${question.max} answers. Uncheck one to choose another.`);
        } else this.questionError('');
        this.saveCurrentSelections();
      }, options);
      this.captureForm.addEventListener('submit', (event) => this.submitContact(event), options);
      this.captureForm.elements.phone.addEventListener('input', () => {
        this.captureForm.elements.phone.setCustomValidity('');
      }, options);
      this.querySelector('[data-capture-back]').addEventListener('click', () => {
        if (this.capturePending) return;
        if (this.dataset.capturePosition === 'beginning') this.showScreen('welcome');
        else this.showQuestion(this.data.questions.length - 1);
      }, options);
      this.querySelector('[data-review]').addEventListener('click', () => this.showQuestion(0), options);
      this.querySelector('[data-restart]').addEventListener('click', () => {
        this.answers = {};
        this.contactCaptured = false;
        this.contactFirstName = '';
        this.emailSignupFailed = false;
        this.captureForm.reset();
        this.captureForm.elements.phone.setCustomValidity('');
        this.showScreen('welcome');
      }, options);
    }

    showScreen(name) {
      for (const screen of this.screens) screen.hidden = screen.dataset.screen !== name;
      this.screen = name;
      this.querySelector('[data-progress-wrap]').hidden = name === 'welcome' || name === 'results';
      const screen = this.screens.find((item) => item.dataset.screen === name);
      const heading = screen.querySelector('[tabindex="-1"]');
      heading?.focus({ preventScroll: true });
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
    }

    updateProgress(value, label, count) {
      const total = this.data.questions.length + 1;
      const bar = this.querySelector('[role="progressbar"]');
      bar.setAttribute('aria-valuemax', total);
      bar.setAttribute('aria-valuenow', value);
      bar.setAttribute('aria-valuetext', label);
      this.querySelector('[data-progress-bar]').style.width = `${value / total * 100}%`;
      this.querySelector('[data-step-label]').textContent = label;
      this.querySelector('[data-step-count]').textContent = count;
    }

    showQuestion(index) {
      this.questionIndex = index;
      const question = this.data.questions[index];
      this.querySelector('[data-question-title]').textContent = question.title;
      const description = this.querySelector('[data-question-description]');
      description.textContent = question.description;
      description.hidden = !question.description;
      this.querySelector('[data-choice-hint]').textContent = question.multiple
        ? `Choose up to ${question.max} answers.` : 'Choose one answer.';
      const choices = this.querySelector('[data-choices]');
      choices.replaceChildren();
      choices.classList.toggle('preme-quiz__choices--pictures', question.choices.some((choice) => choice.image));
      question.choices.forEach((choice, choiceIndex) => {
        const label = document.createElement('label');
        label.className = 'preme-quiz__choice';
        const input = document.createElement('input');
        input.type = question.multiple ? 'checkbox' : 'radio';
        input.name = `quiz-choice-${this.dataset.sectionId}`;
        input.value = choice.id;
        input.dataset.choice = '';
        input.checked = (this.answers[question.id] || []).includes(choice.id);
        label.append(input);
        const key = document.createElement('span');
        key.className = 'preme-quiz__choice-key';
        key.textContent = String.fromCharCode(65 + choiceIndex);
        key.setAttribute('aria-hidden', 'true');
        label.append(key);
        if (choice.image && this.imageURLs[choice.image]) {
          const image = document.createElement('img');
          image.src = this.imageURLs[choice.image];
          image.alt = '';
          image.width = 160;
          image.height = 160;
          label.append(image);
        }
        const text = document.createElement('span');
        text.className = 'preme-quiz__choice-text';
        text.textContent = choice.label;
        label.append(text);
        choices.append(label);
      });
      this.questionError('');
      this.querySelector('[data-next]').innerHTML = index === this.data.questions.length - 1
        ? 'finish my gut check <span aria-hidden="true">→</span>'
        : 'continue <span aria-hidden="true">→</span>';
      const offset = this.dataset.capturePosition === 'beginning' ? 1 : 0;
      this.updateProgress(index + 1 + offset, `question ${index + 1} of ${this.data.questions.length}`, `${index + 1} / ${this.data.questions.length}`);
      this.showScreen('question');
    }

    selectedChoices() {
      return [...this.questionForm.querySelectorAll('[data-choice]:checked')].map((input) => input.value);
    }

    saveCurrentSelections() {
      this.answers[this.data.questions[this.questionIndex].id] = this.selectedChoices();
    }

    questionError(message) {
      const error = this.querySelector('[data-question-error]');
      error.textContent = message;
      error.hidden = !message;
    }

    advance() {
      if (this.questionIndex < this.data.questions.length - 1) this.showQuestion(this.questionIndex + 1);
      else if (!this.contactCaptured) this.showCapture();
      else this.showResults();
    }

    showCapture() {
      const beginning = this.dataset.capturePosition === 'beginning';
      if (beginning) this.querySelector('[data-capture-title]').textContent = 'before we begin.';
      this.querySelector('[data-capture-eyebrow]').textContent = beginning ? 'let’s get to know you' : 'one last step';
      this.querySelector('[data-capture-submit]').textContent = beginning ? 'start my gut check →' : 'see my results →';
      this.querySelector('[data-capture-error]').hidden = true;
      this.updateProgress(beginning ? 1 : this.data.questions.length + 1, beginning ? 'your contact details' : 'unlock your results', 'email + phone');
      this.showScreen('capture');
    }

    async submitContact(event) {
      event.preventDefault();
      if (this.capturePending) return;
      const form = this.captureForm;
      const phone = this.core.normalizePhone(form.elements.phone.value);
      form.elements.phone.setCustomValidity(phone ? '' : 'Enter a valid phone number. Outside the US/Canada, include + and the country code.');
      if (!form.reportValidity()) return;
      if (form.elements.website.value) return;
      const contact = {
        firstName: form.elements.first_name.value.trim(),
        email: form.elements.email.value.trim(),
        phone,
        emailConsent: form.elements.email_consent?.checked || false
      };
      const submit = this.querySelector('[data-capture-submit]');
      const back = this.querySelector('[data-capture-back]');
      const error = this.querySelector('[data-capture-error]');
      this.capturePending = true;
      form.setAttribute('aria-busy', 'true');
      submit.disabled = back.disabled = true;
      submit.textContent = 'saving your details…';
      error.hidden = true;
      try {
        const timeout = new AbortController();
        const timer = setTimeout(() => timeout.abort(), 15000);
        const abortSubmission = () => timeout.abort();
        this.abortController.signal.addEventListener('abort', abortSubmission, { once: true });
        let result;
        try {
          result = this.preview ? { emailSignupFailed: false }
            : await this.core.captureContact(fetch.bind(window), {
              siteId: this.dataset.siteId, listId: this.dataset.listId
            }, contact, timeout.signal);
        } finally {
          clearTimeout(timer);
          this.abortController.signal.removeEventListener('abort', abortSubmission);
        }
        this.contactCaptured = true;
        this.contactFirstName = contact.firstName;
        this.emailSignupFailed = result.emailSignupFailed;
        if (this.dataset.capturePosition === 'beginning') this.showQuestion(0);
        else this.showResults();
      } catch (failure) {
        if (!this.isConnected) return;
        error.textContent = 'We couldn’t save your details. Please check your connection and try again. Your answers are still here.';
        error.hidden = false;
        error.scrollIntoView({ block: 'nearest' });
      } finally {
        this.capturePending = false;
        form.removeAttribute('aria-busy');
        submit.disabled = back.disabled = false;
        submit.textContent = this.dataset.capturePosition === 'beginning' ? 'start my gut check →' : 'see my results →';
      }
    }

    showResults() {
      if (!this.contactCaptured) return;
      const result = this.core.scoreQuiz(this.data, this.answers);
      this.querySelector('[data-result-eyebrow]').textContent = this.contactFirstName
        ? `${this.contactFirstName}, here’s your starting point` : 'your results';
      this.querySelector('[data-result-title]').textContent = result.outcome?.title
        || 'We need a little more to go on.';
      const description = this.querySelector('[data-result-description]');
      description.replaceChildren();
      const text = result.outcome?.description
        || 'Your answers didn’t give us enough information for a personalized recommendation. Review your answers, or explore the full preme collection to find a routine that feels good to you.';
      for (const paragraph of text.split(/\n\s*\n/)) {
        const element = document.createElement('p');
        element.textContent = paragraph;
        description.append(element);
      }
      let hasProducts = false;
      for (const group of this.querySelectorAll('[data-outcome-key]')) {
        const matches = group.dataset.outcomeKey === result.outcome?.key && !hasProducts;
        group.hidden = !matches;
        if (matches && group.querySelector('.preme-quiz__product')) hasProducts = true;
        else if (matches) group.hidden = true;
      }
      this.querySelector('[data-recommendations]').hidden = !hasProducts;
      const notice = this.querySelector('[data-subscription-notice]');
      notice.hidden = !this.emailSignupFailed;
      notice.textContent = 'Your contact details were saved, but your optional email signup couldn’t be completed. You can still view your results.';
      this.showScreen('results');
    }
  }

  customElements.define('preme-gut-quiz', PremeGutQuiz);
})();
